import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { fetchTicket, postTicketMessage, patchTicket } from '@/lib/admin/tickets';
import type { TicketStatus, TicketPriority } from '@/types/admin';
import PageSkeleton from '@/components/PageSkeleton';
import { cn } from '@/lib/cn';

export default function TicketDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reply, setReply] = useState('');

  const { data: ticket, isLoading, isError } = useQuery({
    queryKey: ['admin', 'ticket', id],
    queryFn: () => fetchTicket(id),
    enabled: !!id,
  });

  const sendMut = useMutation({
    mutationFn: (body: string) => postTicketMessage(id, { body }),
    onSuccess: () => {
      setReply('');
      qc.invalidateQueries({ queryKey: ['admin', 'ticket', id] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const patchMut = useMutation({
    mutationFn: (payload: Parameters<typeof patchTicket>[1]) => patchTicket(id, payload),
    onSuccess: () => {
      toast.success(t('common.saved'));
      qc.invalidateQueries({ queryKey: ['admin', 'ticket', id] });
    },
    onError: () => toast.error(t('common.error')),
  });

  if (isLoading) return <PageSkeleton variant="detail" />;
  if (isError || !ticket) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        {t('common.error')}
      </div>
    );
  }

  const messages = ticket.messages ?? [];

  return (
    <div className="space-y-4">
      <Link to="/admin/tickets" className="text-sm text-primary-700 hover:underline">
        ← {t('admin.nav.tickets')}
      </Link>
      <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Thread */}
        <div className="space-y-3 lg:col-span-2">
          <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {messages.length === 0 && (
              <p className="text-sm text-gray-500">{t('common.noResults')}</p>
            )}
            {messages.map((m) => {
              const isAdmin = m.authorType === 'admin';
              return (
                <div key={m.id} className={cn('flex', isAdmin ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[80%] rounded-2xl px-4 py-2 text-sm',
                      isAdmin
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-900',
                    )}
                  >
                    <div className="whitespace-pre-wrap">{m.body}</div>
                    <div
                      className={cn(
                        'mt-1 text-[10px]',
                        isAdmin ? 'text-indigo-200' : 'text-gray-500',
                      )}
                    >
                      {m.author?.email ?? m.authorType} · {new Date(m.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <textarea
              rows={4}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder={t('support.reply')}
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm"
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={sendMut.isPending || !reply.trim()}
                onClick={() => sendMut.mutate(reply.trim())}
                className="rounded-lg bg-primary-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {t('support.send')}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="mb-2 text-sm font-semibold text-gray-700">{t('admin.columns.user')}</h3>
            {ticket.user ? (
              <Link
                to={`/admin/users/${ticket.user.id}`}
                className="text-sm font-medium text-primary-700 hover:underline"
              >
                {ticket.user.email}
              </Link>
            ) : (
              <p className="text-sm text-gray-500">—</p>
            )}
          </div>

          <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                {t('admin.columns.status')}
              </label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={ticket.status}
                onChange={(e) => patchMut.mutate({ status: e.target.value as TicketStatus })}
              >
                {(['OPEN', 'PENDING', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as TicketStatus[]).map((s) => (
                  <option key={s} value={s}>{t(`support.status.${s}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                {t('admin.columns.priority')}
              </label>
              <select
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={ticket.priority}
                onChange={(e) => patchMut.mutate({ priority: e.target.value as TicketPriority })}
              >
                {(['LOW', 'NORMAL', 'HIGH', 'URGENT'] as TicketPriority[]).map((p) => (
                  <option key={p} value={p}>{t(`support.priority.${p}`)}</option>
                ))}
              </select>
            </div>
            <div className="text-xs text-gray-500">
              {t('admin.columns.category')}: <span className="font-medium uppercase">{ticket.category}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
